import { MealCategory } from '@/types/nutrition'

/**
 * Parser for Markdown table format nutrition data.
 *
 * Handles data in format:
 * |FOODS|Calories|Carbs|Fat|Protein|Cholest|Sodium|Sugar|Fiber|
 * |---|---|---|---|---|---|---|---|---|
 * |Breakfast||   |   |   |   |   |   |   |   |
 * |IGA 2% Milk - 2% Milk, 350 milliliter|178|16g|7g|12g|37mg|178mg|16g|0g|
 */
export class MarkdownTableParser {
  private readonly mealCategories: readonly MealCategory[] = [
    'Breakfast',
    'Lunch',
    'Dinner',
    'Late meal',
  ] as const

  /**
   * Converts Markdown table format to space-separated format.
   */
  convertToSpaceSeparated(markdownData: string): string {
    const lines = markdownData.split('\n').map((line) => line.trim())
    const convertedLines: string[] = []

    let foundHeader = false

    for (const line of lines) {
      if (!line) {
        convertedLines.push('')
        continue
      }

      // Skip non-table lines (first line should be date)
      if (!line.startsWith('|')) {
        convertedLines.push(line)
        continue
      }

      // Skip separator line (|---|---|...)
      if (line.includes('---')) {
        continue
      }

      // Parse table row
      const cells = this.parseTableRow(line)

      // Handle header row
      if (!foundHeader && cells[0] === 'FOODS') {
        foundHeader = true
        const headerLine = cells.join('    ')
        convertedLines.push(headerLine)
        continue
      }

      // Handle meal category rows (second column empty)
      if (cells[0] && this.isMealCategory(cells[0])) {
        convertedLines.push(cells[0])
        continue
      }

      // Handle TOTALS row
      if (cells[0] === 'TOTALS') {
        const totalsLine = cells.join('    ')
        convertedLines.push(totalsLine)
        continue
      }

      // Handle food item rows (skip if empty food name or all nutrition values are empty)
      if (cells[0] && !this.isEmptyNutritionRow(cells)) {
        const foodItemLine = cells.join('    ')
        convertedLines.push(foodItemLine)
      }
    }

    return convertedLines.join('\n')
  }

  /**
   * Parses a single table row into cells.
   * Handles malformed tables with extra columns by taking only the first 9 cells.
   */
  private parseTableRow(line: string): string[] {
    // Remove leading and trailing pipes, then split by pipes
    const cleanLine = line.replace(/^\|/, '').replace(/\|$/, '')
    const rawCells = cleanLine.split('|').map((cell) => cell.trim())

    // Convert empty cells or cells with just spaces to actual empty strings
    const cells = rawCells.map((cell) => {
      // Handle cells that are empty or contain only whitespace
      if (!cell || /^\s*$/.test(cell)) {
        return ''
      }
      return cell
    })

    // Handle malformed tables with extra columns by taking only the first 9 cells
    // (FOODS + 8 nutrition columns)
    return cells.slice(0, 9)
  }

  /**
   * Checks if a string is a meal category (standard or time-based).
   * Supports both standard meal names and time-based categories like "00-04", "16-20", "20-24".
   */
  private isMealCategory(text: string): boolean {
    // Check standard meal categories first
    if (this.mealCategories.includes(text as MealCategory)) {
      return true
    }

    // Check for time-based meal categories (e.g., "00-04", "16-20", "20-24")
    const timePattern = /^\d{2}-\d{2}$/
    return timePattern.test(text.trim())
  }

  /**
   * Checks if a nutrition row is effectively empty (all nutrition values are empty or whitespace).
   */
  private isEmptyNutritionRow(cells: string[]): boolean {
    // If we have less than 9 cells (food name + 8 nutrition values), it's malformed
    if (cells.length < 9) {
      return true
    }

    // Check if all nutrition values (cells 1-8) are empty or just whitespace
    const nutritionCells = cells.slice(1, 9)
    return nutritionCells.every((cell) => !cell || /^\s*$/.test(cell))
  }

  /**
   * Detects if input data is in Markdown table format.
   */
  static isMarkdownFormat(data: string): boolean {
    const lines = data.split('\n').map((line) => line.trim())

    // Look for header line starting with |FOODS|
    return lines.some((line) => line.startsWith('|FOODS|') && line.includes('Calories'))
  }
}
